import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '@kognitos/lattice';
import { captureException } from '@/modules/sentry';
import { useTransitionGuideEntryState } from '@/generated/hooks/astral/v1/api';
import { TransitionGuideEntryStateRequestAction } from '@/generated/hooks/astral/models';
import { useCurrentUser } from '@/generated/hooks/agora/v1/user';
import type { IParsedGuideEntry } from '@/shared/utils/chat-utils';
import { parseResourceName } from '@/shared/utils/api-utils';

/**
 * Return type for the useGuideEntryTransition hook.
 */
export interface IUseGuideEntryTransitionReturn {
  /** Approves a pending guide entry */
  approveGuideEntry: (guideEntry: IParsedGuideEntry) => Promise<void>;
  /** Rejects a pending guide entry */
  rejectGuideEntry: (guideEntry: IParsedGuideEntry) => Promise<void>;
  /** Archives an approved guide entry */
  archiveGuideEntry: (guideEntry: IParsedGuideEntry) => Promise<void>;
  /** Whether a transition is in progress */
  isTransitioning: boolean;
}

/**
 * Extracts resource path components from a guide entry name.
 *
 * @param name - Full resource name (e.g., 'organizations/org/workspaces/ws/automations/auto/guideEntries/id')
 * @returns Object with organization, workspace, automation, and guideEntry IDs
 */
function parseGuideEntryName(name: string): {
  organization: string;
  workspace: string;
  automation: string;
  guideEntry: string;
} {
  const parsed = parseResourceName(name);
  return {
    organization: parsed.organization || '',
    workspace: parsed.workspace || '',
    automation: parsed.automation || '',
    guideEntry: parsed.guideEntry || '',
  };
}

/**
 * Hook for managing guide entry state transitions (approve, reject, archive).
 *
 * Uses the TransitionGuideEntryState API to change guide entry states.
 * Provides callbacks for each action with built-in error handling and toast notifications.
 *
 * @returns Object with transition functions and loading state
 *
 * @example
 * const { approveGuideEntry, rejectGuideEntry, archiveGuideEntry, isTransitioning } = useGuideEntryTransition();
 *
 * // In your component:
 * <Button onClick={() => approveGuideEntry(guideEntry)} disabled={isTransitioning}>
 *   Accept
 * </Button>
 */
export function useGuideEntryTransition(): IUseGuideEntryTransitionReturn {
  const { t } = useTranslation('guidance');

  const { mutateAsync: transitionState, isPending: isTransitioning } =
    useTransitionGuideEntryState();
  const { data: currentUser } = useCurrentUser();

  /**
   * Transitions a guide entry to a new state.
   *
   * @param guideEntry - The guide entry to transition
   * @param action - The transition action to perform
   * @param successMessage - Toast message on success
   */
  const transitionGuideEntry = useCallback(
    async (
      guideEntry: IParsedGuideEntry,
      action: TransitionGuideEntryStateRequestAction,
      successMessage: string,
      user?: string,
    ) => {
      const {
        organization,
        workspace,
        automation,
        guideEntry: guideEntryId,
      } = parseGuideEntryName(guideEntry.name);

      if (!organization || !workspace || !automation || !guideEntryId) {
        const error = new Error('Invalid guide entry name');
        toast.error(t('guidance.troubleshooting.widget.transitionError'));
        captureException(error, {
          tags: { module: 'guide-entry-widget', action: 'transition' },
          extra: { guideName: guideEntry.name, action },
        });
        throw error;
      }

      try {
        await transitionState({
          organization,
          workspace,
          automation,
          // Note: "guideEntrie" is a typo in the generated API (should be "guideEntry").
          // This originates from the openapiv2 plugin incorrectly singularizing "guideEntries".
          guideEntrie: guideEntryId,
          data: { action, user },
        });

        toast.success(successMessage);
      } catch (error) {
        toast.error(t('guidance.troubleshooting.widget.transitionError'));
        captureException(error, {
          tags: { module: 'guide-entry-widget', action: 'transition' },
          extra: { guideName: guideEntry.name, action },
        });
        throw error; // Re-throw so widget can handle appropriately
      }
    },
    [transitionState, t],
  );

  /**
   * Approves a pending guide entry.
   * Transitions from PENDING_APPROVAL to APPROVED.
   */
  const approveGuideEntry = useCallback(
    async (guideEntry: IParsedGuideEntry) => {
      await transitionGuideEntry(
        guideEntry,
        TransitionGuideEntryStateRequestAction.ACTION_APPROVE,
        t('guidance.troubleshooting.widget.acceptedMessage'),
        currentUser?.data?.user?.name,
      );
    },
    [transitionGuideEntry, t, currentUser],
  );

  /**
   * Rejects a pending guide entry.
   * Transitions from PENDING_APPROVAL to REJECTED.
   */
  const rejectGuideEntry = useCallback(
    async (guideEntry: IParsedGuideEntry) => {
      await transitionGuideEntry(
        guideEntry,
        TransitionGuideEntryStateRequestAction.ACTION_REJECT,
        t('guidance.troubleshooting.widget.rejectedMessage'),
      );
    },
    [transitionGuideEntry, t],
  );

  /**
   * Archives a guide entry.
   * Can be performed from any state.
   */
  const archiveGuideEntry = useCallback(
    async (guideEntry: IParsedGuideEntry) => {
      await transitionGuideEntry(
        guideEntry,
        TransitionGuideEntryStateRequestAction.ACTION_ARCHIVE,
        t('guidance.troubleshooting.widget.archivedMessage'),
      );
    },
    [transitionGuideEntry, t],
  );

  return {
    approveGuideEntry,
    rejectGuideEntry,
    archiveGuideEntry,
    isTransitioning,
  };
}
